import { useLocation } from '@solidjs/router';
import { Show, createMemo } from 'solid-js';
import { ACTIONS, useResources } from '~/types/permissions/permission';
import { PermissionGuard } from './PermissionGuard';
import type { Component, ParentProps } from 'solid-js';
import type { FlattenedResource } from '~/types/permissions/permission';

const matchesPath = (pathname: string, routePath: string) => {
  if (routePath === '/') {
    return pathname === routePath;
  }

  return pathname === routePath || pathname.startsWith(`${routePath}/`);
};

const findResourceByPath = (pathname: string, resources: FlattenedResource[]) => {
  const normalizedPathname = pathname.toLowerCase();
  let bestMatch: { resource: string; action: string } | null = null;
  let longestMatchLength = 0;

  for (const resource of resources) {
    if (!resource.routePath) continue;

    const routePath = resource.routePath.toLowerCase();
    if (matchesPath(normalizedPathname, routePath) && routePath.length > longestMatchLength) {
      longestMatchLength = routePath.length;
      bestMatch = { resource: resource.fullName, action: ACTIONS.READ };
    }
  }

  return bestMatch;
};

export const AutoPermissionGuard: Component<ParentProps> = (props) => {
  const location = useLocation();
  const { flattenedResources } = useResources();

  const permissionRequirement = createMemo(() => findResourceByPath(location.pathname, flattenedResources()));

  return (
    <Show when={permissionRequirement()} fallback={<>{props.children}</>}>
      <PermissionGuard resource={permissionRequirement()!.resource} action={permissionRequirement()!.action}>
        {props.children}
      </PermissionGuard>
    </Show>
  );
};
